import { useContext, useEffect, useMemo, useState } from "react";
import { GlassInputControl, GlassInputControlLabel, GlassLayerGroupSelect, GlassLayerSelect } from "./GlassInput";
import { findSquareFromMap, SquareSelection, SquareSelectorMap, squareToMapGeom } from "./MapSquareSelector";
import { MapitStateContext } from "src/states/MapitState";
import { LayerVariabilityType } from "src/common/managers/Types";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { featureCollection } from "@turf/helpers";
import { message } from "./Message";
import { Localization } from "@viamap/viamap2-common";


export function AreaSelector({ onChange }: { onChange: (layerIds?: number[], feature?: GeoJSON.FeatureCollection<GeoJSON.Geometry>) => void }) {
   const [modus, setModus] = useState<"square" | "layer" | "folder">("layer");
   const { state: mapitState } = useContext(MapitStateContext);
   const [selectedLayerId, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [selectedSquare, setSelectedSquare] = useState<SquareSelection | undefined>(undefined);
   const [selectedLayerGroupId, setSelectedLayerGroupId] = useState<number | undefined>(undefined);

   const selectableLayers = useMemo(() => {
      let options = Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
      setSelectedLayerId((prev) => prev ? prev : options.length > 0 ? options[0].layerId : undefined);
      return options;
   }, [mapitState.layers])

   useEffect(() => {
      onLayerSelect(selectableLayers[0]?.layerId);
   }, [])

   function onChangeModus(modus: "square" | "layer" | "folder") {
      setModus(modus);
      switch (modus) {
         case "square":
            onChangeSquare(selectedSquare);
            break;
         case "layer":
            onLayerSelect(selectedLayerId);
            break;
         case "folder":
            onLayerGroupSelect(selectedLayerGroupId);
            break;
      }
   }

   function resetSquare() {
      let map = mapitState.map?.getMapPop();
      let newSquare = findSquareFromMap(map);
      if (!newSquare) {
         message.error("Could not find map bounds");
         return;
      }
      onChangeSquare(newSquare);
   }

   function onChangeSquare(square: SquareSelection | undefined) {
      setSelectedSquare(square);
      if (square) {
         onChange(undefined, featureCollection(squareToMapGeom(square).features?.filter(f => f.geometry?.type !== "Point") ?? []));
      } else {
         let map = mapitState.map?.getMapPop();
         onChange(undefined, featureCollection(squareToMapGeom(findSquareFromMap(map)).features?.filter(f => f.geometry?.type !== "Point") ?? []));
      }
   }

   function onLayerSelect(layerId: number | undefined) {
      setSelectedLayerId(layerId);
      if (layerId === undefined) {
         if (selectableLayers.length > 0) {
            const layer = selectableLayers[0];
            onChange([layer.layerId], layer?.geoJson ?? { type: "FeatureCollection", features: [] });
            return;
         }
         onChange(undefined, { type: "FeatureCollection", features: [] });
         return;
      }
      const layer = mapitState.layers[layerId];
      if (layer) {
         onChange([layerId], layer?.geoJson ?? { type: "FeatureCollection", features: [] });
      } else {
         onChange(undefined, { type: "FeatureCollection", features: [] });
      }
   }

   function onLayerGroupSelect(groupId?: number, layerGroupId?: number[]) {
      setSelectedLayerGroupId(groupId);
      if (layerGroupId === undefined) {
         onChange(undefined, { type: "FeatureCollection", features: [] });
         return;
      }
      let layers = layerGroupId.flatMap(id => selectableLayers.find(layer => layer.layerId === id) ?? []).filter((a) => a?.visible);
      onChange(layers.map(layer => layer.layerId), { type: "FeatureCollection", features: layers.flatMap(layer => layer?.geoJson?.features ?? []).filter(Boolean) });
   }

   return (
      <>
         <GlassInputControlLabel>{""}</GlassInputControlLabel>
         <div className="Horizontal_Radio_Button_group">
            <button className={modus === "layer" ? "active" : ""} onClick={() => onChangeModus("layer")}><MIBIcon name="layer" icon={"layer"} /></button>
            <button className={modus === "folder" ? "active" : ""} onClick={() => onChangeModus("folder")}><MIBIcon name="folder" icon={"layerGroup"} /></button>
            <button className={modus === "square" ? "active" : ""} onClick={() => onChangeModus("square")}><MIBIcon name="square" icon={"simplify"} /></button>
         </div>
         {{
            "square":
               <>
                  <GlassInputControlLabel>Box</GlassInputControlLabel>
                  <GlassInputControl><span >W: {selectedSquare?.w.toFixed(0) ?? 0}m</span>
                     <span >H: {selectedSquare?.h.toFixed(0) ?? 0}m</span></GlassInputControl>
                  <SquareSelectorMap active={true} value={selectedSquare} onChange={onChangeSquare} />
               </>,
            "layer":
               <>
                  <GlassInputControlLabel>{Localization.getText("Search layer")}</GlassInputControlLabel>
                  <GlassLayerSelect selected={selectedLayerId} onSelect={onLayerSelect} layers={selectableLayers} />
               </>,
            "folder":
               <>
                  <GlassInputControlLabel>{Localization.getText("Group")}</GlassInputControlLabel>
                  <GlassLayerGroupSelect selected={selectedLayerGroupId} onSelect={onLayerGroupSelect} layers={selectableLayers} layerHierarchy={mapitState.layerHierarchy} />
               </>
         }[modus]}
      </>
   )
}
